import { LumberPrice } from '@paragon/contract-library';
import { designService, NewLumberPrice } from '@paragon/data-connections';
import { getLoggedInUser } from '@paragon/login-tools';
import { getLumberCode } from '@paragon/material-lists';
import { enqueueError } from '@paragon/notification-tools';
import { addSpinnerMessage, removeSpinnerMessage } from '@paragon/task-spinner';

import { setLumberInventory } from '../store/lumberInventoriesSlice';
import { setLumberPrices } from '../store/lumberPricesSlice';
import { AsyncThunkActionBoolean, AsyncThunkActionVoid } from './thunkActions';

export const getAllLumberPrices = (): AsyncThunkActionVoid => async (dispatch) => {
  const spinnerId = dispatch(addSpinnerMessage('Loading Lumber Prices'));

  try {
    const lumberPrices = await designService.lumberPrices.getAllLumberPrices();
    dispatch(setLumberPrices(lumberPrices));
  } catch {
    dispatch(enqueueError('Error loading lumber prices'));
  } finally {
    dispatch(removeSpinnerMessage(spinnerId));
  }
};

export const importLumberPrices =
  (file: any): AsyncThunkActionVoid =>
  async (dispatch) => {
    const spinnerId = dispatch(addSpinnerMessage('Importing Lumber Prices'));

    try {
      const lumberPriceRecords = await designService.lumberPrices.importLumberPrices(file);
      dispatch(setLumberPrices(lumberPriceRecords));
    } catch (exception) {
      if (typeof exception === 'string') {
        dispatch(enqueueError(exception));
      } else {
        dispatch(enqueueError('Error importing'));
      }
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };

export const getLumberInventory = (): AsyncThunkActionVoid => async (dispatch, getState) => {
  const [defaultLumbers, materialSet] = await Promise.all([
    designService.lumbers.getDefaultLumberInventory(),
    designService.materialLists.getOrganizationMaterialSet(getLoggedInUser(getState())!.organization),
  ]);
  const organizationLumbers = Object.values(materialSet.lumberLists).flatMap((lumberList) =>
    lumberList.stockLumbers.map((stockLumber) => stockLumber.lumber),
  );
  const lumbers = new Map([...defaultLumbers, ...organizationLumbers].map((lumber) => [getLumberCode(lumber), lumber]));
  dispatch(setLumberInventory([...lumbers.values()]));
};

export const insertLumberPrice =
  (newLumberPrice: NewLumberPrice): AsyncThunkActionBoolean =>
  async (dispatch) => {
    const spinnerId = dispatch(addSpinnerMessage('Adding Lumber Price'));

    try {
      const addedLumberPrice = await designService.lumberPrices.postLumberPrice(newLumberPrice);
      dispatch(setLumberPrices([addedLumberPrice]));
      return true;
    } catch (error) {
      console.error(error);
      dispatch(enqueueError('Error inserting lumber price'));
      return false;
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };

export const editLumberPrices =
  (lumberPrices: readonly LumberPrice[]): AsyncThunkActionBoolean =>
  async (dispatch) => {
    const spinnerId = dispatch(
      addSpinnerMessage(lumberPrices.length === 1 ? 'Editing Price' : `Editing ${lumberPrices.length} Lumber Prices`),
    );

    try {
      const edits = lumberPrices.map((lumberPrice) => ({
        guid: lumberPrice.guid,
        pricePerThousandBoardFeet: lumberPrice.pricePerThousandBoardFeet,
        notes: lumberPrice.notes,
      }));
      const editedLumberPrices = await designService.lumberPrices.editLumberPrices(edits);
      dispatch(setLumberPrices(editedLumberPrices));
      return true;
    } catch {
      dispatch(
        enqueueError(
          lumberPrices.length === 1
            ? 'Error editing lumber price'
            : `Error editing ${lumberPrices.length} lumber prices`,
        ),
      );
      return false;
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };
